import annaDsbUpi from '../../../infrastructure/constants/functions/annaDsbUpi';
import annaDsbUpiEnrichment from '../../../infrastructure/constants/functions/annaDsbUpiEnrichment';
import exchangeRates from '../../../infrastructure/constants/functions/exchangeRates';
import firdsFcaInstrument from '../../../infrastructure/constants/functions/firdsFcaInstrument';
import firdsEsmaInstrument from '../../../infrastructure/constants/functions/firdsEsmaInstrument';
import isin from '../../../infrastructure/constants/functions/isin';
import lei from '../../../infrastructure/constants/functions/lei';
import lse from '../../../infrastructure/constants/functions/lse';
import operandType from '../../../infrastructure/constants/functions/operandType';
import { endpointFilterType } from '../../../infrastructure/constants/report/endpointFilterType';
import ButtonContext from '../../../infrastructure/helpers/common/ButtonContext';
import aggregationHelper from '../../../infrastructure/helpers/functions/aggregation/aggregationHelper';
import reportFieldHelper from '../../../infrastructure/helpers/functions/common/reportFieldHelper';
import selectorHelper from '../../../infrastructure/helpers/functions/selectors/SelectorHelper';
import accuracyValidationValidator from '../../../infrastructure/helpers/functions/validator/accuracyValidationValidator';
import aggregationValidator from '../../../infrastructure/helpers/functions/validator/aggregationValidator';
import caseValidator from '../../../infrastructure/helpers/functions/validator/caseValidator';
import dictionaryValidator from '../../../infrastructure/helpers/functions/validator/dictionaryValidator';
import endpointFilterValidator from '../../../infrastructure/helpers/functions/validator/endpointFilterValidator';
import enrichmentValidator from '../../../infrastructure/helpers/functions/validator/enrichmentValidator';
import filterValidator from '../../../infrastructure/helpers/functions/validator/filterValidator';
import lookupValidator from '../../../infrastructure/helpers/functions/validator/lookupValidator';
import matchingKeyValidator from '../../../infrastructure/helpers/functions/validator/matchingKeyValidator';
import selectorValidator from '../../../infrastructure/helpers/functions/validator/selectorValidator';
import userDefinedFunctionValidator from '../../../infrastructure/helpers/functions/validator/userDefinedFunctionValidator';
import variableValidator from '../../../infrastructure/helpers/functions/validator/variableValidator';
import reportConfigNavigation from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import { htmlIds } from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation.HtmlIds';
import ReportCollections from '../../../infrastructure/types/Functions/ReportCollections';
import Aggregation from '../../../types/report/Aggregation';
import ReportConfig from '../../../types/report/ReportConfig';
import Selector from '../../../types/report/Selector';
import EndpointFilter from '../../../types/report/EndpointFilter';
import accuracyValidationKind from '../../../infrastructure/constants/accuracyValidationKind';
import { reportType } from '../../../infrastructure/constants/reportType';
import dataSourceValidators from '../DataSources/DataSourceForm.Validation';
import { navigationToDictionaryField, navigationToParameter } from '../UserDefinedFunctions/UserDefinedFunctionSelector/UserDefinedFunction.Helpers';

const validate = (
    config: ReportConfig,
    collections: ReportCollections,
    onAggregationErrorClick: (aggregationNumber: number, field?: number) => void,
    onIsinSelectorErrorClick: (isinSelectorNumber: number) => void,
    onLeiSelectorErrorClick: (isinSelectorNumber: number) => void,
    onExchangeRatesSelectorErrorClick: (exchangeRateselectorNumber: number) => void,
    onAnnaDsbUpiEnrichmentSelectorErrorClick: (annaDsbUpiEnrichmentSelectorNumber: number) => void,
    onAnnaDsbUpiSelectorErrorClick: (annaDsbUpiSelectorNumber: number) => void,
    onLSESelectorErrorClick: (lseSelectorNumber: number) => void,
    onFCARegulatedEntitiesSelectorErrorClick: (fcaRegulatedEntitiesSelectorNumber: number) => void,
    onFirdsFcaInstrumentSelectorErrorClick: (firdsInstrumentSelectorNumber: number) => void,
    onFirdsEsmaInstrumentSelectorErrorClick: (firdsEsmaInstrumentSelectorNumber: number) => void,
    onCaseFieldErrorClick: (caseNumber: number, fieldNumber: number) => void
): ButtonContext[] => {

    let errors: ButtonContext[] = [];

    let dataSourceError = dataSourceValidators.validateDataSources(config);
    if (dataSourceError) {
        errors.push({ content: dataSourceError, onClick: () => reportConfigNavigation.navigateToSection(htmlIds.dataSources) });
    }

    if (config.type !== reportType.globalModule) {

        let aggregationNumbers = aggregationValidator.validate(config, collections.dataSources, collections.globalModuleConfig.userDefinedFunctions);
        if (aggregationNumbers.length > 0) {

            aggregationNumbers.forEach(number => {

                let aggr = config.aggregations.find(a => a.number === number) as Aggregation;

                let name = aggregationHelper.fallbackName(aggr);

                let errorField = aggr.errors.length === 0 ? aggr.fields.find(f => f.errors.length > 0) : null;

                errors.push({ content: `Invalid aggregation '${name}'`, onClick: () => onAggregationErrorClick(number, errorField?.fieldId) });
            });
        }

        let isinSelectorsNumbers = selectorValidator.validate(config.isinSelectors, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, 'ISIN', 'name', isin.type, config.type);
        if (isinSelectorsNumbers.length > 0) {

            isinSelectorsNumbers.forEach(number => {

                let a = config.isinSelectors.find(a => a.number === number) as Selector;

                let name = selectorHelper.fallbackName(a, 'ISIN');

                errors.push({ content: `Invalid ISIN '${name}'`, onClick: () => onIsinSelectorErrorClick(number) });
            });
        }

        let leiSelectorsNumbers = selectorValidator.validate(config.leiSelectors, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, 'LEI', 'name', lei.type, config.type);
        if (leiSelectorsNumbers.length > 0) {

            leiSelectorsNumbers.forEach(number => {

                let a = config.leiSelectors.find(a => a.number === number) as Selector;

                let name = selectorHelper.fallbackName(a, 'LEI');

                errors.push({ content: `Invalid LEI '${name}'`, onClick: () => onLeiSelectorErrorClick(number) });
            });
        }

        let exchangeRatesSelectorsNumbers = selectorValidator.validate(config.exchangeRatesSelectors, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, 'ExchangeRates', 'name', exchangeRates.type, config.type);
        if (exchangeRatesSelectorsNumbers.length > 0) {

            exchangeRatesSelectorsNumbers.forEach(number => {

                let a = config.exchangeRatesSelectors.find(a => a.number === number) as Selector;

                let name = selectorHelper.fallbackName(a, 'ExchangeRates');

                errors.push({ content: `Invalid Exchange Rates '${name}'`, onClick: () => onExchangeRatesSelectorErrorClick(number) });
            });
        }

        let firdsFcaInstrumentSelectorsNumbers = selectorValidator.validate(config.firdsFcaInstrumentSelectors, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, 'FirdsInstrument', 'name', firdsFcaInstrument.type, config.type);

        let isThereFirdsInstrumentsError = false;

        if (firdsFcaInstrumentSelectorsNumbers.length > 0) {

            firdsFcaInstrumentSelectorsNumbers.forEach(number => {

                let a = config.firdsFcaInstrumentSelectors.find(a => a.number === number) as Selector;

                let name = selectorHelper.fallbackName(a, 'FirdsFcaInstrument');

                errors.push({ content: `Invalid Firds FcaInstrument '${name}'`, onClick: () => onFirdsFcaInstrumentSelectorErrorClick(number) });

                isThereFirdsInstrumentsError = true;
            });
        }

        if (!isThereFirdsInstrumentsError) {
            let selectors = config.firdsFcaInstrumentSelectors;

            selectors.forEach((item) => {
                if (item.keys?.[1].assignment.value.operandType === undefined ||
                    (item.keys?.[1].assignment.value.operandType === operandType.constant &&
                        !item.keys?.[1].assignment.value.value)) {
                    errors.push({ content: `Invalid Firds Fca Instrument '${item.name}'. Venue must have value.`, onClick: () => onFirdsFcaInstrumentSelectorErrorClick(item.number) });
                }

                if (item.keys?.[0].assignment.value.operandType === undefined ||
                    (item.keys?.[0].assignment.value.operandType === operandType.constant &&
                        !item.keys?.[0].assignment.value.value)) {
                    errors.push({ content: `Invalid Firds Fca Instrument '${item.name}'. InstrumentName must be empty string or have some value.`, onClick: () => onFirdsFcaInstrumentSelectorErrorClick(item.number) });
                }
            });
        }

        let firdsEsmaInstrumentSelectorsNumbers = selectorValidator.validate(config.firdsEsmaInstrumentSelectors, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, 'FirdsInstrument', 'name', firdsEsmaInstrument.type, config.type);

        isThereFirdsInstrumentsError = false;

        if (firdsEsmaInstrumentSelectorsNumbers.length > 0) {

            firdsEsmaInstrumentSelectorsNumbers.forEach(number => {

                let a = config.firdsEsmaInstrumentSelectors.find(a => a.number === number) as Selector;

                let name = selectorHelper.fallbackName(a, 'FirdsEsmaInstrument');

                errors.push({ content: `Invalid Firds Esma Instrument '${name}'`, onClick: () => onFirdsEsmaInstrumentSelectorErrorClick(number) });

                isThereFirdsInstrumentsError = true;
            });
        }

        if (!isThereFirdsInstrumentsError) {
            let selectors = config.firdsEsmaInstrumentSelectors;

            selectors.forEach((item) => {
                if (item.keys?.[1].assignment.value.operandType === undefined ||
                    (item.keys?.[1].assignment.value.operandType === operandType.constant &&
                        !item.keys?.[1].assignment.value.value)) {
                    errors.push({ content: `Invalid Firds Esma Instrument '${item.name}'. Venue must have value.`, onClick: () => onFirdsEsmaInstrumentSelectorErrorClick(item.number) });
                }

                if (item.keys?.[0].assignment.value.operandType === undefined ||
                    (item.keys?.[0].assignment.value.operandType === operandType.constant &&
                        !item.keys?.[0].assignment.value.value)) {
                    errors.push({ content: `Invalid Firds Esma Instrument '${item.name}'. InstrumentName must be empty string or have some value.`, onClick: () => onFirdsEsmaInstrumentSelectorErrorClick(item.number) });
                }
            });
        }

        let annaDsbUpiEnrichmentSelectorsNumbers = selectorValidator.validate(config.annaDsbUpiEnrichmentSelectors, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, 'AnnaDsbUpiEnrichment', 'name', annaDsbUpiEnrichment.type, config.type);

        let isThereAnnaDsbUpiEnrichmentError = false;

        if (annaDsbUpiEnrichmentSelectorsNumbers.length > 0) {

            annaDsbUpiEnrichmentSelectorsNumbers.forEach(number => {

                let a = config.annaDsbUpiEnrichmentSelectors.find(a => a.number === number) as Selector;

                let name = selectorHelper.fallbackName(a, 'AnnaDsbUpiEnrichment');

                errors.push({ content: `Invalid Anna Dsb Upi Enrichment '${name}'`, onClick: () => onAnnaDsbUpiEnrichmentSelectorErrorClick(number) });

                isThereAnnaDsbUpiEnrichmentError = true;
            });
        }

        if (!isThereAnnaDsbUpiEnrichmentError) {

            let annaDsbUpiEnrichmentSelectors = config.annaDsbUpiEnrichmentSelectors;

            annaDsbUpiEnrichmentSelectors.forEach((item) => {
                if (item.keys?.[0].assignment.value.operandType === undefined ||
                    (item.keys?.[0].assignment.value.operandType === operandType.constant &&
                        !item.keys?.[0].assignment.value.value)) {
                    errors.push({ content: `Invalid Anna Dsb Upi Enrichment '${item.name}'. CFI must have some value.`, onClick: () => onAnnaDsbUpiEnrichmentSelectorErrorClick(item.number) });
                }

                if (item.keys?.[1].assignment.value.operandType === undefined) {
                    errors.push({ content: `Invalid Anna Dsb Upi Enrichment '${item.name}'. ISIN must have some value or to be a empty string.`, onClick: () => onAnnaDsbUpiEnrichmentSelectorErrorClick(item.number) });
                }
            });
        }

        let annaDsbUpiSelectorsNumbers = selectorValidator.validate(config.annaDsbUpiSelectors, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, 'AnnaDsbUpi', 'name', annaDsbUpi.type, config.type);
        if (annaDsbUpiSelectorsNumbers.length > 0) {

            annaDsbUpiSelectorsNumbers.forEach(number => {

                let a = config.annaDsbUpiSelectors.find(a => a.number === number) as Selector;

                let name = selectorHelper.fallbackName(a, 'AnnaDsbUpi');

                errors.push({ content: `Invalid Anna Dsb Upi '${name}'`, onClick: () => onAnnaDsbUpiSelectorErrorClick(number) });
            });
        }

        let lseSelectorsNumbers = selectorValidator.validate(config.lseSelectors, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, 'LSE', 'name', lse.type, config.type);
        if (lseSelectorsNumbers.length > 0) {

            lseSelectorsNumbers.forEach(number => {

                let a = config.lseSelectors.find(a => a.number === number) as Selector;

                let name = selectorHelper.fallbackName(a, 'LSE');

                errors.push({ content: `Invalid LSE '${name}'`, onClick: () => onLSESelectorErrorClick(number) });
            });
        }

        let fcaRegulatedEntitiesSelectorsNumbers = selectorValidator.validate(config.fcaRegulatedEntitiesSelectors, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, 'FCARegulatedEntities', 'name', lse.type, config.type);
        if (fcaRegulatedEntitiesSelectorsNumbers.length > 0) {

            fcaRegulatedEntitiesSelectorsNumbers.forEach(number => {

                let a = config.fcaRegulatedEntitiesSelectors.find(a => a.number === number) as Selector;

                let name = selectorHelper.fallbackName(a, 'FCARegulatedEntities');

                errors.push({ content: `Invalid FCA Regulated Entities '${name}'`, onClick: () => onFCARegulatedEntitiesSelectorErrorClick(number) });
            });
        }
    }

    let error = lookupValidator.validate(config.lookups, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.dictionaries, collections.dataSources, collections.reportFields, collections.customDataSourceFields, config.type);
    if (error.message) {
        let htmlId = reportConfigNavigation.buildLookupId(error.componentNumber as number);

        errors.push({ content: error.message, onClick: () => reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.lookups) });
    }

    error = userDefinedFunctionValidator.validateFunctions(config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.type);
    if (error.message) {

        let htmlId = reportConfigNavigation.buildFunctionId(error.mainConmponentNumber as number);

        errors.push({ content: error.message, onClick: () => reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.userDefinedFunctions) });
    }

    error = userDefinedFunctionValidator.validateFunctionParameters(config.userDefinedFunctions);
    if (error.message) {
        errors.push({
            content: error.message,
            onClick: () => navigationToParameter(error.mainConmponentNumber as number, error.componentNumber as number)
        });
    }

    error = variableValidator.validate(config.variables, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections.dataSources, collections.reportFields, collections.customDataSourceFields, config.type);
    if (error.message) {
        let htmlId = reportConfigNavigation.buildVariableId(error.componentNumber as number);

        errors.push({ content: error.message as string, onClick: () => reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.variables) });
    }

    if (config.type !== reportType.globalModule) {

        error = dictionaryValidator.validateDictionaries(config.dictionaries);
        if (error.message) {

            let htmlId = reportConfigNavigation.buildDictionaryId(error.componentNumber as number);

            errors.push({ content: error.message as string, onClick: () => reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.dictionaries) });
        }

        error = dictionaryValidator.validateDictionaryFields(config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.dictionaries, collections.dataSources, config.type);
        if (error.message) {

            let htmlId = reportConfigNavigation.buildDictionaryId(error.mainConmponentNumber as number);

            let fieldId = reportConfigNavigation.buildDictionaryFieldId(error.componentNumber as number);

            errors.push({
                content: error.message as string, onClick: () => {
                    reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.dictionaries);

                    reportConfigNavigation.navigateToItemInSection(`${fieldId}-${htmlId}`, htmlIds.dictionaryFields);
                }
            });
        }

        error = filterValidator.validate(config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, config.variables, config.filters, collections.dataSources, collections.customDataSourceFields, collections.reportFields, config.type);
        if (error.message) {

            let htmlId = reportConfigNavigation.buildFilterId(error.componentNumber as number);

            errors.push({ content: error.message, onClick: () => reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.filters) });
        }

        error = endpointFilterValidator.validateByType(config.endpointFilters, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections, endpointFilterType.regular, [], config.type);
        if (error.message) {

            let htmlId = reportConfigNavigation.buildEndpointFilterId(config.endpointFilters.find(f => f.number === error.componentNumber) as EndpointFilter);

            errors.push({ content: error.message, onClick: () => reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.endpointFilters) });
        }

        error = endpointFilterValidator.validateByType(config.endpointFilters, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections, endpointFilterType.underReport, config.variables, config.type);
        if (error.message) {

            let htmlId = reportConfigNavigation.buildUnderReportFilter(error.componentNumber as number);

            errors.push({ content: error.message, onClick: () => reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.expectedUnderReport) });
        }

        error = endpointFilterValidator.validateByType(config.endpointFilters, config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, collections, endpointFilterType.overReport, [], config.type);
        if (error.message) {

            let htmlId = reportConfigNavigation.buildOverReportFilter(error.componentNumber as number);

            errors.push({ content: error.message, onClick: () => reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.expectedOverReport) });
        }

        error = caseValidator.validate(config, collections.dataSources, collections.customDataSourceFields, collections.globalModuleConfig.userDefinedFunctions);
        if (error.message) {

            let htmlId = reportConfigNavigation.buildCaseId(error.componentNumber as number);

            errors.push({ content: error.message, onClick: () => reportConfigNavigation.navigateToItemInSection(htmlId, htmlIds.cases) });
        }

        let enrichmentErrors = enrichmentValidator.validate(config.userDefinedFunctions, collections.globalModuleConfig.userDefinedFunctions, config.lookups, config.dictionaries, config.variables, config.cases, collections.dataSources, collections.reportFields, collections.customDataSourceFields, config.type);

        enrichmentErrors.forEach(e => {

            let definition = reportFieldHelper.getById(collections.reportFields, e.fieldId);

            errors.push({

                content: `Case ${e.caseNumber}, Field ${definition.name}`,

                onClick: () => onCaseFieldErrorClick(e.caseNumber, definition.id)
            });
        });

        let invalidValidation = accuracyValidationValidator.validate(config.validations, config.userDefinedFunctions, config.lookups, config.dictionaries, config.variables, collections.dataSources, collections.reportFields, collections.customDataSourceFields, config.regime, config.type);
        if (invalidValidation?.errors) {

            let htmlId = reportConfigNavigation.buildValidationId(invalidValidation.number, invalidValidation.kind);

            let validationKind = invalidValidation.kind === accuracyValidationKind.standard ? htmlIds.cnValidations : htmlIds.coreValidations;

            errors.push({
                content: 'Invalid validation(s)', onClick: () => {
                    reportConfigNavigation.navigateToSection(htmlIds.validations);
                    reportConfigNavigation.navigateToItemInSection(htmlId, validationKind);
                }
            });
        }

        let matchingKeyError = matchingKeyValidator.validate(config.matchingKey);
        if (matchingKeyError) {
            errors.push({ content: matchingKeyError, onClick: () => reportConfigNavigation.navigateToSection(htmlIds.matchingKeys) });
        }

        let matchingKeyEndpointError = matchingKeyValidator.validate(config.matchingKeyEndPoint);
        if (matchingKeyEndpointError) {
            errors.push({ content: matchingKeyEndpointError, onClick: () => reportConfigNavigation.navigateToSection(htmlIds.matchingKeys) });
        }
    }

    return errors;
};

export default validate;